import { useScroll, useTransform, motion, AnimatePresence } from 'framer-motion'
import { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { useLocation, Link, useNavigate } from 'react-router-dom'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import JourneyProgress from '@/components/JourneyProgress'
import ModuleSection from '@/components/ModuleSection'
import FeaturedModuleSection from '@/components/FeaturedModuleSection'
import MasterclassCard from '@/components/MasterclassCard'
import ScrollReveal from '@/components/ScrollReveal'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Sparkles,
  Zap,
  BookOpen,
  ChevronDown,
  ChevronUp,
  Loader2,
  Heart,
  Share2,
} from 'lucide-react'
import { useDrops, useToggleDropLike, useRecordDropView } from '@/services/drops.service'

/** Extrai o ID do vídeo de uma URL do YouTube (shorts ou normal) */
function getYoutubeVideoId(url: string): string | null {
  const match = url.match(/(?:youtube\.com\/shorts\/|youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/)
  return match ? match[1] : null
}
import { useModules, useAuth } from '@/hooks'
import { useToast } from '@/hooks/use-toast'
import {
  useCourses,
  useMyEnrolledCourses,
  useNextEpisode,
  useLastWatched,
} from '@/hooks/useCourses'
import { useCourseProgress } from '@/hooks/use-progress'
import type { EpisodeProgressMap } from '@/components/FeaturedModuleSection'
import { getEpisodeIcon } from '@/utils/episodeIcons'
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import AutoplayPlugin from 'embla-carousel-autoplay'

const Index = () => {
  const heroRef = useRef<HTMLDivElement>(null)
  const [showMoreModules, setShowMoreModules] = useState(false)
  const [activeDrop, setActiveDrop] = useState<number | null>(null)
  const [hoveredDrop, setHoveredDrop] = useState<number | null>(null)
  const hoveredDropTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [dropsCarouselApi, setDropsCarouselApi] = useState<CarouselApi | null>(null)
  const [dropsHighlighted, setDropsHighlighted] = useState(false)
  const dropsAutoplay = useRef(
    AutoplayPlugin({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: true })
  )
  const dropsPlugins = useMemo(() => (activeDrop ? [] : [dropsAutoplay.current]), [activeDrop])

  const location = useLocation()
  const navigate = useNavigate()

  // Auth
  const { user, isAuthenticated } = useAuth()
  const { toast } = useToast()

  // Drops
  const { data: drops } = useDrops()
  const toggleLike = useToggleDropLike()
  const recordView = useRecordDropView()

  // Busca cursos matriculados do usuário
  const {
    data: enrolledCourses,
    isLoading: isLoadingCourses,
    error: coursesError,
  } = useMyEnrolledCourses()

  // Pega o curso mais recente (já vem ordenado por enrolledAt DESC)
  const latestCourse = enrolledCourses?.[0]

  // Busca próximo episódio do curso mais recente
  const { data: nextEpisode, isLoading: isLoadingNextEpisode } = useNextEpisode(
    latestCourse?.id || 0
  )

  // Busca o último episódio assistido (para o Welcome Card)
  const { data: lastWatched } = useLastWatched()

  // Busca progresso do usuário no curso mais recente
  const { data: courseProgress } = useCourseProgress(latestCourse?.id)

  // Monta mapa de progresso: episodeId → { completed, progressPercent }
  const progressMap: EpisodeProgressMap = {}
  if (courseProgress) {
    for (const ep of courseProgress) {
      const percent =
        ep.duration && ep.duration > 0
          ? Math.round((ep.watchTime / ep.duration) * 100)
          : ep.completed
            ? 100
            : 0
      progressMap[ep.episodeId] = {
        completed: ep.completed,
        progressPercent: Math.min(percent, 100),
      }
    }
  }

  // Busca todos os módulos (fallback para usuários não logados)
  const { data: allModules, isLoading: isLoadingModules } = useModules()

  // Masterclasses da seção da home — publicadas + em breve
  const { data: masterclasses, isLoading: isLoadingMasterclasses } = useCourses({
    type: 'masterclass',
    status: 'published,coming_soon',
  })

  // Determina o texto do botão baseado no progresso
  const isCompleted = lastWatched?.progressPercent === 100

  const getJourneyButtonText = useCallback(() => {
    // Se não está autenticado ou não tem cursos, sempre "Começar"
    if (!isAuthenticated || !enrolledCourses || enrolledCourses.length === 0) {
      return 'Começar Jornada'
    }

    // Se completou 100%, oferecer recomeçar
    if (isCompleted) {
      return 'Recomeçar Jornada'
    }

    // Se tem nextEpisode e é o primeiro episódio
    if (nextEpisode?.isFirstEpisode) {
      return 'Começar Jornada'
    }

    // Se tem nextEpisode mas não é o primeiro = já tem progresso
    if (nextEpisode && !nextEpisode.isFirstEpisode) {
      return 'Continuar Jornada'
    }

    // Fallback: Começar
    return 'Começar Jornada'
  }, [isAuthenticated, enrolledCourses, nextEpisode, isCompleted])

  // Handler do botão "Começar/Continuar Jornada"
  const handleStartJourney = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()

      // Caso 1: Usuário não autenticado → Redireciona para sign up
      if (!isAuthenticated) {
        navigate('/auth')
        return
      }

      // Caso 2: Usuário sem cursos matriculados → Scroll para módulos
      if (!enrolledCourses || enrolledCourses.length === 0) {
        const element = document.getElementById('modulos')
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
        return
      }

      // Caso 3: Completou 100% → Recomeçar (primeiro episódio)
      if (isCompleted && latestCourse?.modules && latestCourse.modules.length > 0) {
        const firstModule = latestCourse.modules[0]
        const firstEpisode = firstModule.episodes?.[0]
        if (firstEpisode) {
          navigate(`/episode/${firstModule.id}/${firstEpisode.number}`)
        }
        return
      }

      // Caso 4: Usuário com curso em andamento
      if (nextEpisode) {
        // Se tem próximo episódio definido, vai para ele
        const url = `/episode/${nextEpisode.moduleId}/${nextEpisode.episodeNumber}`
        navigate(url)
      } else if (latestCourse?.modules && latestCourse.modules.length > 0) {
        // Fallback: primeiro episódio do primeiro módulo
        const firstModule = latestCourse.modules[0]
        const firstEpisode = firstModule.episodes?.[0]

        if (firstEpisode) {
          navigate(`/episode/${firstModule.id}/${firstEpisode.number}`)
        } else {
          // Se não tem episódios, scroll para módulos
          const element = document.getElementById('modulos')
          element?.scrollIntoView({ behavior: 'smooth' })
        }
      }
    },
    [isAuthenticated, enrolledCourses, nextEpisode, latestCourse, isCompleted, navigate]
  )

  // Filtra apenas módulos de cursos tipo 'regular' e publicados
  const modules =
    allModules?.filter(
      (module) =>
        module.course?.type === 'regular' &&
        module.course?.status === 'published' &&
        module.status === 'published'
    ) || []

  // Scroll to hash section when navigating from other pages
  // Re-runs when drops load since #drops section is conditionally rendered
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash)
      if (element) {
        setTimeout(() => {
          const headerOffset = 80
          const elementPosition = element.getBoundingClientRect().top + window.scrollY
          window.scrollTo({ top: elementPosition - headerOffset, behavior: 'smooth' })
        }, 100)
      }
      if (location.hash === '#drops') {
        setDropsHighlighted(true)
        setTimeout(() => setDropsHighlighted(false), 2500)
      }
    }
  }, [location.hash, drops])

  useEffect(() => {
    const handler = (e: CustomEvent) => {
      if (e.detail === 'drops') {
        setDropsHighlighted(true)
        setTimeout(() => setDropsHighlighted(false), 2500)
      }
    }
    window.addEventListener('nav-section-click', handler as EventListener)
    return () => window.removeEventListener('nav-section-click', handler as EventListener)
  }, [])

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  })

  const backgroundY = useTransform(scrollYProgress, [0, 1], ['0%', '50%'])
  const textY = useTransform(scrollYProgress, [0, 1], ['0%', '100%'])
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.8, 0.3])

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Welcome Card + Drops — wrapped with parallax background */}
      <div className="relative overflow-hidden">
        {/* Parallax background layer */}
        <div ref={heroRef} className="absolute inset-0 pointer-events-none">
          <motion.div className="gradient-hero absolute inset-0" style={{ y: backgroundY }} />
          <motion.div
            className="absolute top-20 left-10 w-64 h-64 bg-primary/5 rounded-full blur-3xl"
            style={{ y: useTransform(scrollYProgress, [0, 1], ['0%', '80%']) }}
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute bottom-10 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl"
            style={{ y: useTransform(scrollYProgress, [0, 1], ['0%', '60%']) }}
            animate={{ scale: [1, 1.3, 1], opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          />
          <motion.div
            className="absolute top-1/4 right-1/4 w-4 h-4 bg-primary/40 rounded-full"
            animate={{ y: [0, -20, 0], x: [0, 10, 0], opacity: [0.4, 0.8, 0.4] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute top-1/3 left-1/4 w-3 h-3 bg-primary/30 rounded-full"
            animate={{ y: [0, 15, 0], x: [0, -8, 0], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
          />
          <motion.div
            className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-primary/50 rounded-full"
            animate={{ y: [0, -12, 0], opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          />
          <motion.div
            className="absolute top-[15%] right-[15%] w-5 h-5 bg-primary/40 rounded-full"
            animate={{ y: [0, -15, 0], opacity: [0.3, 0.6, 0.3] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            className="absolute top-[25%] left-[10%] w-4 h-4 bg-primary/30 rounded-full"
            animate={{ y: [0, 20, 0], opacity: [0.2, 0.5, 0.2] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
          />
          <motion.div
            className="absolute bottom-[25%] right-[10%] w-6 h-6 bg-primary/35 rounded-full"
            animate={{ y: [0, -18, 0], x: [0, 8, 0], opacity: [0.25, 0.55, 0.25] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          />
          <motion.div
            className="absolute bottom-[35%] left-[18%] w-3 h-3 bg-primary/25 rounded-full"
            animate={{ y: [0, 12, 0], x: [0, -6, 0], opacity: [0.2, 0.45, 0.2] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 0.8 }}
          />
        </div>

        {/* Welcome Card */}
        {isAuthenticated && (
          <div className="container pt-6">
            <style>{`
            @keyframes shimmer-flow {
              0% { background-position: 200% center; }
              100% { background-position: 0% center; }
            }
            @keyframes glow-pulse {
              0%, 100% { box-shadow: 0 0 30px 6px rgba(242, 101, 34, 0.18); }
              50%       { box-shadow: 0 0 50px 12px rgba(242, 101, 34, 0.32); }
            }
            .shimmer-gold {
              background: linear-gradient(
                90deg,
                #F26522 0%,
                #F26522 35%,
                #ffaa60 50%,
                #F26522 65%,
                #F26522 100%
              );
              background-size: 200% auto;
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              background-clip: text;
              animation: shimmer-flow 3.5s linear infinite;
            }
            .welcome-card {
              background:
                radial-gradient(circle at 90% 10%, rgba(242, 101, 34, 0.12) 0%, transparent 55%),
                linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 50%, #1c1008 100%);
              border: 1px solid #252525;
              animation: glow-pulse 3s ease-in-out infinite;
            }
          `}</style>
            <div className="welcome-card rounded-2xl px-12 py-12 flex items-center justify-between gap-8">
              {/* Left — motivação */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground mb-1">Bem-vindo de volta 👋</p>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
                  <span className="text-white">{user?.name?.split(' ')[0] || 'Aluno'}, </span>
                  <span className="shimmer-gold">
                    {(lastWatched?.completedEpisodes ?? 0) > 0
                      ? 'continue sua jornada'
                      : 'inicie sua jornada'}
                  </span>
                </h2>
                {lastWatched && (
                  <p className="mt-2" style={{ fontSize: '13px' }}>
                    <span style={{ color: '#666', fontWeight: 400 }}>Último assistido: </span>
                    <span style={{ color: '#ccc', fontWeight: 700 }}>
                      Ep. {lastWatched.moduleOrder}.{lastWatched.episodeNumber} —{' '}
                      {lastWatched.episodeTitle}
                    </span>
                  </p>
                )}
                <button
                  className="continue-btn mt-4"
                  onClick={handleStartJourney}
                  style={{
                    backgroundColor: '#F26522',
                    color: '#fff',
                    fontFamily: 'Poppins, sans-serif',
                    fontWeight: 700,
                    fontSize: '13px',
                    letterSpacing: '0.04em',
                    padding: '10px 20px',
                    borderRadius: '10px',
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background-color 0.15s ease, transform 0.15s ease',
                  }}
                  onMouseEnter={(e) => {
                    ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = '#d55a1e'
                    ;(e.currentTarget as HTMLButtonElement).style.transform = 'translateY(-1px)'
                  }}
                  onMouseLeave={(e) => {
                    ;(e.currentTarget as HTMLButtonElement).style.backgroundColor = '#F26522'
                    ;(e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)'
                  }}
                >
                  ▶ {getJourneyButtonText()}
                </button>
              </div>

              {/* Right — dado */}
              <div className="hidden md:flex flex-col items-start gap-2 shrink-0 min-w-[140px]">
                <p
                  style={{
                    fontSize: '10px',
                    color: '#999',
                    letterSpacing: '0.12em',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                  }}
                >
                  Progresso geral
                </p>
                <div className="flex items-end gap-1 leading-none">
                  <span
                    style={{ fontSize: '52px', fontWeight: 800, color: '#F26522', lineHeight: 1 }}
                  >
                    {lastWatched?.progressPercent ?? 0}
                  </span>
                  <span
                    style={{
                      fontSize: '16px',
                      fontWeight: 400,
                      color: '#7a3a10',
                      marginBottom: '6px',
                    }}
                  >
                    %
                  </span>
                </div>
                {/* Barra */}
                <div
                  className="w-full rounded-full overflow-hidden"
                  style={{ height: '5px', background: '#1f1f1f' }}
                >
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${lastWatched?.progressPercent ?? 0}%`,
                      background: 'linear-gradient(90deg, #F26522, #ff8040)',
                    }}
                  />
                </div>
                <p style={{ fontSize: '11px', color: '#888' }}>
                  {lastWatched?.completedEpisodes ?? 0} de {lastWatched?.totalEpisodes ?? 0}{' '}
                  episódios concluídos
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Overlay fumê quando um drop está ativo */}
        {activeDrop && (
          <div
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0,0,0,0.8)' }}
            onClick={() => setActiveDrop(null)}
          />
        )}

        {/* Drops Section */}
        {isAuthenticated && drops && drops.length > 0 && (
          <div id="drops" className={`container mt-6 ${activeDrop ? 'relative z-50' : ''}`}>
            <style>{`
            .drops-scroll::-webkit-scrollbar { height: 4px; }
            .drops-scroll::-webkit-scrollbar-track { background: transparent; }
            .drops-scroll::-webkit-scrollbar-thumb { background: #F26522; border-radius: 999px; }
            .drop-card { transition: transform 0.15s ease, border-color 0.15s ease; }
            .drop-card:hover { transform: translateX(3px); border-color: #F26522 !important; }
            .drop-card.inactive:hover { transform: none !important; border-color: #1e1e1e !important; cursor: default; }
          `}</style>

            {/* Cabeçalho */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <p style={{ fontSize: '25px', fontWeight: 700 }} className="text-foreground">
                  ⚡ Drops
                </p>
                <p style={{ fontSize: '16px', color: '#555' }}>
                  Você ainda mais inteligente com microaprendizados práticos de IA.
                </p>
              </div>
              <button
                style={{
                  fontSize: '12px',
                  color: '#888',
                  border: '1px solid #222',
                  background: 'transparent',
                  borderRadius: '8px',
                  padding: '6px 14px',
                  cursor: 'pointer',
                }}
              >
                Ver todos
              </button>
            </div>

            {/* Carrossel */}
            <div className="px-12">
              <Carousel
                setApi={setDropsCarouselApi}
                opts={{ align: 'start', loop: true, duration: 80 }}
                plugins={dropsPlugins}
                className="w-full"
              >
                <CarouselContent className="-ml-4">
                  {(drops || []).map((drop, idx) => {
                    const isActive = activeDrop === drop.id
                    const tags =
                      drop.tags
                        ?.split(',')
                        .map((t) => t.trim())
                        .filter(Boolean) || []
                    const firstTag = tags[0]
                    const videoId = getYoutubeVideoId(drop.videoUrl)
                    return (
                      <CarouselItem key={drop.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                        <div
                          className={`drop-card${activeDrop && !isActive ? ' inactive' : ''} relative cursor-pointer overflow-hidden w-full`}
                          style={{
                            aspectRatio: '9/16',
                            background: '#161616',
                            border: `1px solid ${isActive ? '#F26522' : '#1e1e1e'}`,
                            borderRadius: '14px',
                          }}
                          onClick={() => {
                            if (isActive) {
                              setActiveDrop(null)
                            } else {
                              const total = drops?.length ?? 1
                              const prevIdx = (idx - 1 + total) % total
                              dropsCarouselApi?.scrollTo(prevIdx)
                              setActiveDrop(drop.id)
                              recordView.mutate(drop.id)
                            }
                          }}
                          onMouseEnter={() => {
                            if (hoveredDropTimeout.current) clearTimeout(hoveredDropTimeout.current)
                            setHoveredDrop(drop.id)
                          }}
                          onMouseLeave={() => {
                            hoveredDropTimeout.current = setTimeout(
                              () => setHoveredDrop(null),
                              2500
                            )
                          }}
                        >
                          {/* Ativo: embed com autoplay. Inativo: thumbnail estática */}
                          {isActive && videoId ? (
                            <iframe
                              src={`https://www.youtube.com/embed/${videoId}?autoplay=1&controls=1&modestbranding=1&rel=0&loop=1&playlist=${videoId}`}
                              title={drop.title}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope"
                              allowFullScreen
                              className="absolute inset-0 w-full h-full"
                              style={{ borderRadius: '14px', border: 'none', zIndex: 3 }}
                            />
                          ) : (
                            <>
                              {/* Thumbnail: custom ou do YouTube */}
                              {(drop.thumbnailUrl || videoId) && (
                                <img
                                  src={
                                    drop.thumbnailUrl ||
                                    `https://img.youtube.com/vi/${videoId}/0.jpg`
                                  }
                                  alt={drop.title}
                                  className="absolute inset-0 w-full h-full object-cover"
                                  style={{ borderRadius: '14px' }}
                                />
                              )}
                              {/* Ícone play centralizado */}
                              <div
                                style={{
                                  position: 'absolute',
                                  inset: 0,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: '28px',
                                  color: '#fff',
                                  textShadow: '0 2px 8px rgba(0,0,0,0.6)',
                                  transition: 'color 0.15s ease',
                                }}
                              >
                                ▶
                              </div>
                            </>
                          )}

                          {/* Gradiente inferior */}
                          <div
                            style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '55%',
                              background:
                                'linear-gradient(to top, rgba(0,0,0,0.92) 0%, transparent 100%)',
                            }}
                          />

                          {/* Badge assistido */}
                          {drop.watched && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                width: '17px',
                                height: '17px',
                                background: '#1a3a1a',
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '9px',
                                color: '#6bc741',
                                zIndex: 2,
                              }}
                            >
                              ✓
                            </div>
                          )}

                          {/* Dim sobre cards não ativos */}
                          {activeDrop && !isActive && (
                            <div
                              style={{
                                position: 'absolute',
                                inset: 0,
                                background: 'rgba(0,0,0,0.65)',
                                zIndex: 4,
                                borderRadius: '14px',
                                transition: 'opacity 0.2s ease',
                              }}
                            />
                          )}

                          {/* Menu lateral — like e compartilhar */}
                          {isActive && hoveredDrop === drop.id && (
                            <motion.div
                              initial={{ opacity: 0, x: 10 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 10 }}
                              transition={{ duration: 0.2, delay: 0.15 }}
                              style={{
                                position: 'absolute',
                                right: '35px',
                                bottom: '140px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '20px',
                                zIndex: 5,
                              }}
                            >
                              {/* Like */}
                              <button
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '4px',
                                }}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  toggleLike.mutate(drop.id, {
                                    onSuccess: (data) => {
                                      toast({
                                        title: data.liked ? '❤️ Curtido!' : 'Like removido',
                                        description: data.liked
                                          ? `Você curtiu "${drop.title}"`
                                          : `Você removeu o like de "${drop.title}"`,
                                      })
                                    },
                                  })
                                }}
                              >
                                <Heart
                                  style={{
                                    width: '30px',
                                    height: '30px',
                                    color: drop.liked ? '#F26522' : '#fff',
                                    fill: drop.liked ? '#F26522' : 'none',
                                    filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))',
                                    transition: 'color 0.15s ease, fill 0.15s ease',
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: '10px',
                                    color: '#fff',
                                    fontWeight: 600,
                                    textShadow: '0 1px 3px rgba(0,0,0,0.6)',
                                  }}
                                >
                                  {drop._count.drop_likes}
                                </span>
                              </button>
                            </motion.div>
                          )}

                          {/* Conteúdo sobreposto */}
                          <div
                            style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              padding: '12px',
                              zIndex: 2,
                            }}
                          >
                            <p
                              style={{
                                fontSize: '24px',
                                fontWeight: 600,
                                lineHeight: 1.4,
                                color: drop.watched ? '#888' : '#ddd',
                                marginBottom: '8px',
                              }}
                            >
                              {drop.title}
                            </p>
                            <div className="flex items-center justify-between">
                              {firstTag && (
                                <span
                                  style={{
                                    fontSize: '9px',
                                    fontWeight: 700,
                                    color: '#F26522',
                                    background: 'rgba(30,30,30,0.85)',
                                    padding: '2px 6px',
                                    borderRadius: '4px',
                                  }}
                                >
                                  {firstTag}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </CarouselItem>
                    )
                  })}
                </CarouselContent>
                <CarouselPrevious className="-left-10" />
                <CarouselNext className="-right-10" />
              </Carousel>
            </div>
          </div>
        )}
      </div>
      {/* end parallax wrapper */}

      {/* Featured Modules Section - User's Latest Course */}
      <section id="modulos" className="pt-12 pb-0 relative z-10 bg-background">
        {isAuthenticated && (
          <div className="container">
            <div className="welcome-card rounded-2xl overflow-hidden">
              {/* Título */}
              <div className="px-6 pt-6 pb-4">
                <h2 className="text-2xl font-bold text-white">📚 Seu Progresso</h2>
              </div>

              {/* Loading State */}
              {isLoadingCourses && (
                <div className="flex flex-col items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
                  <span className="text-muted-foreground">Carregando seus cursos...</span>
                </div>
              )}

              {/* Logged In - With Courses and Modules */}
              {!isLoadingCourses &&
                latestCourse &&
                latestCourse.modules &&
                latestCourse.modules.length > 0 && (
                  <>
                    {latestCourse.modules.map((module, moduleIndex) => {
                      const validEpisodes =
                        module.episodes?.filter((ep) => ep.title && ep.number) || []
                      if (validEpisodes.length === 0) return null
                      return (
                        <FeaturedModuleSection
                          key={module.id}
                          moduleId={module.id}
                          moduleOrder={module.order || moduleIndex + 1}
                          title={module.title}
                          description={module.description || ''}
                          courseTitle={latestCourse.title}
                          isLatestCourse={true}
                          progressMap={progressMap}
                          episodes={validEpisodes.map((episode) => ({
                            id: episode.id,
                            number: episode.number,
                            title: episode.title,
                            icon: getEpisodeIcon(episode.title, moduleIndex, episode.number),
                            thumbnailUrl: episode.thumbnailUrl,
                            status: episode.status,
                          }))}
                        />
                      )
                    })}
                  </>
                )}

              {/* Logged In - No Courses */}
              {!isLoadingCourses && (!enrolledCourses || enrolledCourses.length === 0) && (
                <div className="text-center py-16 px-6">
                  <div className="max-w-md mx-auto space-y-4">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <BookOpen className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-2xl font-bold text-foreground">
                      Você ainda não está matriculado em nenhum curso
                    </h3>
                    <p className="text-muted-foreground">
                      Explore nosso catálogo e comece sua jornada de aprendizado em IA!
                    </p>
                    <Link to="/cursos" className="btn-primary inline-flex items-center gap-2 mt-4">
                      <Sparkles className="w-4 h-4" />
                      Explorar Cursos
                    </Link>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Not Logged In */}
        {!isAuthenticated && (
          <div className="container text-center py-16">
            <div className="max-w-md mx-auto space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-primary" />
              </div>
              <h3 className="text-2xl font-bold text-foreground">
                Faça login para acessar seus cursos
              </h3>
              <p className="text-muted-foreground">
                Entre na plataforma para ver seus módulos e continuar sua jornada de aprendizado.
              </p>
              <Link to="/auth" className="btn-primary inline-flex items-center gap-2 mt-4">
                <Zap className="w-4 h-4" />
                Fazer Login
              </Link>
            </div>
          </div>
        )}
      </section>

      {/* Masterclasses Section */}
      <section id="masterclasses" className="pt-16 pb-16 mt-10 relative z-10 bg-background">
        <div className="container">
          <ScrollReveal>
            <div className="text-center mb-12">
              <span className="module-badge mb-4">Premium</span>
              <h2
                className="text-4xl md:text-5xl font-semibold mt-4 text-gradient"
                style={{ fontFamily: 'Caveat, cursive' }}
              >
                Masterclasses
              </h2>
              <p className="text-muted-foreground mt-2 max-w-xl mx-auto">
                Aulas aprofundadas com especialistas do mercado para levar seus conhecimentos ao
                próximo nível.
              </p>
            </div>
          </ScrollReveal>
          <div className="px-12">
            {isLoadingMasterclasses ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="rounded-lg border border-border bg-card overflow-hidden">
                    <Skeleton className="aspect-video w-full rounded-none" />
                    <div className="p-4 space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-5 w-1/3" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-5/6" />
                    </div>
                  </div>
                ))}
              </div>
            ) : masterclasses && masterclasses.length > 0 ? (
              <Carousel
                opts={{ align: 'start', loop: true }}
                plugins={[
                  AutoplayPlugin({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: true }),
                ]}
                className="w-full"
              >
                <CarouselContent className="-ml-4">
                  {masterclasses.map((course) => (
                    <CarouselItem key={course.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                      <MasterclassCard course={course} />
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="-left-10" />
                <CarouselNext className="-right-10" />
              </Carousel>
            ) : (
              <p className="text-center text-muted-foreground py-12">
                Novas masterclasses em breve.
              </p>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Index
